/**
 * MyAttendanceWidget (v36) — Personal check-in / check-out pill for the dashboard header.
 *
 * Visible to all logged-in staff EXCEPT super_admin (no personal attendance) and platform_admin.
 * Shows today's status (Not marked / Checked In since … / Checked Out at …) plus the
 * appropriate action button.
 *
 * Built deliberately compact to fit alongside theme + seed buttons in the header.
 */
import { useEffect, useState } from 'react';
import { attendanceAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { LogIn, LogOut, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (t) => (t || '').slice(0, 5); // "HH:MM:SS" → "HH:MM"

export const MyAttendanceWidget = () => {
  const { user } = useAuth();
  const [todayRow, setTodayRow] = useState(null);  // { check_in, check_out, ... } or null
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Super Admin and users without a hospital don't need personal attendance
  const enabled = !!user && !user.is_super_admin && !!user.hospital_id;

  const load = async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      // Use the existing /attendance/report endpoint (already RBAC-scoped to the user themself
      // for non-admin callers) for today only.
      const res = await api.get('/attendance/report', {
        params: { start_date: todayISO(), end_date: todayISO(), user_id: user.id },
      });
      const row = (res.data || []).find(r => r.date === todayISO()) || null;
      setTodayRow(row);
    } catch {
      // Some role gates may block /report — fall back to a silent empty state
      setTodayRow(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id, user?.hospital_id]);

  const handleMarkIn = async () => {
    setSubmitting(true);
    try {
      const res = await attendanceAPI.markIn({});
      toast.success(`Checked in at ${fmt(res.data?.check_in)}`);
      setTodayRow(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkOut = async () => {
    setSubmitting(true);
    try {
      const res = await attendanceAPI.markOut({});
      toast.success(`Checked out at ${fmt(res.data?.check_out)} · ${res.data?.work_hours}h logged`);
      setTodayRow(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark out');
    } finally {
      setSubmitting(false);
    }
  };

  if (!enabled) return null;
  if (loading && !todayRow) {
    return (
      <div className="hidden md:inline-flex items-center gap-1.5 text-xs text-slate-400 px-2.5 py-1 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <Loader2 size={12} className="animate-spin" /> Attendance…
      </div>
    );
  }

  const checkedIn = !!todayRow?.check_in;
  const checkedOut = !!todayRow?.check_out;

  if (checkedOut) {
    return (
      <div className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 px-2.5 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40" data-testid="my-attendance-done">
        <CheckCircle2 size={13} className="text-emerald-600" />
        Day done · {fmt(todayRow.check_in)} – {fmt(todayRow.check_out)}
      </div>
    );
  }

  if (checkedIn) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleMarkOut}
        disabled={submitting}
        className="h-8 text-xs gap-1.5 border-amber-300 dark:border-amber-700/40 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
        title={`Checked in at ${fmt(todayRow.check_in)}`}
        data-testid="my-attendance-mark-out-btn">
        {submitting ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
        Mark Out
        <span className="ml-1 text-[10px] opacity-80 hidden lg:inline">since {fmt(todayRow.check_in)}</span>
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleMarkIn}
      disabled={submitting}
      className="h-8 text-xs gap-1.5 border-teal-300 dark:border-teal-700/40 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20"
      title="Mark today's check-in"
      data-testid="my-attendance-mark-in-btn">
      {submitting ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />}
      Mark In
      <Clock size={11} className="opacity-70" />
    </Button>
  );
};

export default MyAttendanceWidget;
